import { useQueryClient } from '@tanstack/react-query';
import {
  AssignmentTypeEnum,
  CaseTypeEnum,
  DashboardLayoutDTO,
  DashboardLayoutWidgetDTO,
  GroupingLevel,
} from 'api/model';
import {
  getUserDashboardLayoutGetMyLayoutQueryKey,
  useUserDashboardLayoutGetMyLayout,
  useUserDashboardLayoutSaveMyLayout,
} from 'api/user-dashboard-layout/user-dashboard-layout';
import { WidgetMetadata } from 'components/Dashboard/types';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocalStorage } from 'usehooks-ts';

interface DashboardState<T> {
  collapsed: boolean;
  expanded: boolean;
  widgets: WidgetMetadata<T>[];
  daterange: [Date | null, Date | null];
  selectedBusinessUnits: number[];
  selectedCaseTypes: CaseTypeEnum[];
  searchText: string;
  selectedAssignmentTypes: AssignmentTypeEnum[];
  selectedSupplierIDs: number[];
  selectedCustomerNames: string[];
  selectedFromCountyIDs: number[];
  selectedToCountyIDs: number[];
  groupingLevel: GroupingLevel;
  excludeCompanyNameContains: string | null;
}

interface DashboardControlsProps<T> {
  dashboardState: DashboardState<T>;
  setCollapsedState: (collapse: boolean) => void;
  setExpandedState: (expand: boolean) => void;
  setWidgets: (widgets: WidgetMetadata<T>[]) => void;
  setSelectedDateRange(daterange: [Date | null, Date | null]): void;
  setSelectedBusinessUnits: (businessUnits: number[]) => void;
  setSelectedCaseTypes: (caseTypes: number[]) => void;
  setSearchText: (text: string) => void;
  setSelectedAssignmentTypes: (assignmentTypes: AssignmentTypeEnum[]) => void;
  setSelectedSupplierIDs: (supplierIDs: number[]) => void;
  setSelectedCustomerNames: (customerNames: string[]) => void;
  setSelectedFromCountyIDs: (countyIDs: number[]) => void;
  setSelectedToCountyIDs: (countyIDs: number[]) => void;
  setGroupingLevel: (level: GroupingLevel) => void;
  setExcludeCompanyNameContains: (pattern: string | null) => void;
}

const getInitialDashboardState = <T,>(): DashboardState<T> => ({
  collapsed: false,
  expanded: false,
  widgets: [],
  daterange: [new Date(), new Date()],
  selectedBusinessUnits: [],
  selectedCaseTypes: [],
  searchText: '',
  selectedAssignmentTypes: [],
  selectedSupplierIDs: [],
  selectedCustomerNames: [],
  selectedFromCountyIDs: [],
  selectedToCountyIDs: [],
  groupingLevel: 1 as GroupingLevel, // Day
  excludeCompanyNameContains: null,
});

const DashboardControlsContext = createContext<
  DashboardControlsProps<any> | undefined
>({
  dashboardState: getInitialDashboardState(),
  setCollapsedState: () => {},
  setExpandedState: () => {},
  setWidgets: () => {},
  setSelectedDateRange: () => {},
  setSelectedBusinessUnits: () => {},
  setSelectedCaseTypes: () => {},
  setSearchText: () => {},
  setSelectedAssignmentTypes: () => {},
  setSelectedSupplierIDs: () => {},
  setSelectedCustomerNames: () => {},
  setSelectedFromCountyIDs: () => {},
  setSelectedToCountyIDs: () => {},
  setGroupingLevel: () => {},
  setExcludeCompanyNameContains: () => {},
});

interface DashboardControlsProviderProps<T> {
  storageKey: string;
  defaultWidgets: WidgetMetadata<T>[];
  /**
   * Saves the widget layout (order and visibility) per user on the server under this key, and
   * keeps the user's order instead of resetting it to `defaultWidgets` order. Without it the
   * layout lives only in this browser's localStorage.
   */
  serverLayoutKey?: string;
  children: React.ReactNode;
}

type SavedWidget<T> = Pick<WidgetMetadata<T>, 'identifier' | 'active'>;

/**
 * Combines a saved layout with the current `defaultWidgets`: titles always come from the
 * defaults, widgets that no longer exist are dropped and new widgets get their default state.
 * With `keepSavedOrder` the saved order wins and new widgets are appended; otherwise the order
 * is that of `defaultWidgets`.
 */
const mergeWithDefaults = <T,>(
  saved: SavedWidget<T>[],
  defaultWidgets: WidgetMetadata<T>[],
  keepSavedOrder: boolean
): WidgetMetadata<T>[] => {
  const savedActive = new Map(saved.map((w) => [w.identifier, w.active]));
  const withSavedActive = (widget: WidgetMetadata<T>) => ({
    ...widget,
    active: savedActive.get(widget.identifier) ?? widget.active,
  });

  if (!keepSavedOrder) {
    return defaultWidgets.map(withSavedActive);
  }

  const defaultsById = new Map(defaultWidgets.map((w) => [w.identifier, w]));
  const savedIds = saved
    .map((w) => w.identifier)
    .filter((id, i, ids) => defaultsById.has(id) && ids.indexOf(id) === i);

  return [
    ...savedIds.map((id) => defaultsById.get(id)!),
    ...defaultWidgets.filter((w) => !savedActive.has(w.identifier)),
  ].map(withSavedActive);
};

export const DashboardControlsProvider = <T,>({
  storageKey,
  defaultWidgets,
  serverLayoutKey,
  children,
}: DashboardControlsProviderProps<T>) => {
  const keepSavedOrder = serverLayoutKey !== undefined;
  const [dashboardState, setDashboardState] = useLocalStorage<
    DashboardState<T>
  >(`${storageKey}.DashboardState`, {
    ...getInitialDashboardState(),
    widgets: defaultWidgets,
  });

  useEffect(() => {
    setDashboardState((prevState) => ({
      ...prevState,
      // Update dashboardState when defaultWidgets changes (ie we add a new widget that the user does not have yet in their local storage).
      // But don't overwrite the user's previous selection of active state for the widgets.
      widgets: mergeWithDefaults(
        prevState.widgets,
        defaultWidgets,
        keepSavedOrder
      ),
    }));
  }, [defaultWidgets, keepSavedOrder, setDashboardState]);

  // The server layout is the source of truth; localStorage only makes the first render instant.
  const serverLayoutParams = { dashboardKey: serverLayoutKey ?? '' };
  const { data: serverLayout } = useUserDashboardLayoutGetMyLayout(
    serverLayoutParams,
    {
      query: {
        enabled: serverLayoutKey !== undefined,
        staleTime: Infinity,
        refetchOnWindowFocus: false,
      },
    }
  );

  useEffect(() => {
    // An empty layout means the user has not saved one yet: keep what this browser has.
    if (!serverLayout?.widgets?.length) return;

    const saved = serverLayout.widgets.map(
      (w): SavedWidget<T> => ({
        identifier: w.identifier as unknown as T,
        active: w.active,
      })
    );
    setDashboardState((prevState) => ({
      ...prevState,
      widgets: mergeWithDefaults(saved, defaultWidgets, true),
    }));
  }, [serverLayout, defaultWidgets, setDashboardState]);

  const queryClient = useQueryClient();
  const { mutate: saveServerLayout } = useUserDashboardLayoutSaveMyLayout();

  const [daterange, setDateRange] = useState<[Date | null, Date | null]>([
    new Date(
      new Date().getFullYear(),
      new Date().getMonth(),
      new Date().getDate(),
      0,
      0,
      0,
      0
    ),
    new Date(
      new Date().getFullYear(),
      new Date().getMonth(),
      new Date().getDate(),
      23,
      59,
      59,
      999
    ),
  ]);

  const setWidgets = useCallback(
    (widgets: WidgetMetadata<T>[]) => {
      setDashboardState((state) => ({ ...state, widgets }));

      if (serverLayoutKey === undefined) return;

      const layout: DashboardLayoutDTO = {
        dashboardKey: serverLayoutKey,
        widgets: widgets.map(
          (w): DashboardLayoutWidgetDTO => ({
            identifier: w.identifier as unknown as number,
            active: w.active,
          })
        ),
      };
      // Update the cached layout too, so that coming back to the view does not re-apply the
      // layout fetched before this change.
      queryClient.setQueryData(
        getUserDashboardLayoutGetMyLayoutQueryKey({
          dashboardKey: serverLayoutKey,
        }),
        layout
      );
      saveServerLayout({ data: layout });
    },
    [setDashboardState, serverLayoutKey, queryClient, saveServerLayout]
  );

  const setCollapsedState = useCallback(
    (collapse: boolean) => {
      setDashboardState((state) => ({ ...state, collapsed: collapse }));
    },
    [setDashboardState]
  );

  const setExpandedState = useCallback(
    (expand: boolean) => {
      setDashboardState((state) => ({ ...state, expanded: expand }));
    },
    [setDashboardState]
  );

  const setSelectedDateRange = useCallback(
    (range: [Date | null, Date | null]) => {
      const startDate = range[0]
        ? new Date(
            range[0].getFullYear(),
            range[0].getMonth(),
            range[0].getDate(),
            0,
            0,
            0,
            0
          )
        : null;

      const endDate = range[1]
        ? new Date(
            range[1].getFullYear(),
            range[1].getMonth(),
            range[1].getDate(),
            23,
            59,
            59,
            999
          )
        : null;

      setDateRange([startDate, endDate]);
    },
    []
  );

  const setSelectedBusinessUnits = useCallback(
    (businessUnits: number[]) => {
      setDashboardState((state) => ({
        ...state,
        selectedBusinessUnits: Array.from(businessUnits),
      }));
    },
    [setDashboardState]
  );

  const setSelectedCaseTypes = useCallback(
    (assignmentTypes: number[]) => {
      setDashboardState((state) => ({
        ...state,
        selectedCaseTypes: Array.from(assignmentTypes) as CaseTypeEnum[],
      }));
    },
    [setDashboardState]
  );

  const setSearchText = useCallback(
    (text: string) => {
      setDashboardState((state) => ({
        ...state,
        searchText: text,
      }));
    },
    [setDashboardState]
  );

  const setSelectedAssignmentTypes = useCallback(
    (assignmentTypes: AssignmentTypeEnum[]) => {
      setDashboardState((state) => ({
        ...state,
        selectedAssignmentTypes: Array.from(assignmentTypes),
      }));
    },
    [setDashboardState]
  );

  const setSelectedSupplierIDs = useCallback(
    (supplierIDs: number[]) => {
      setDashboardState((state) => ({
        ...state,
        selectedSupplierIDs: Array.from(supplierIDs),
      }));
    },
    [setDashboardState]
  );

  const setSelectedCustomerNames = useCallback(
    (customerNames: string[]) => {
      setDashboardState((state) => ({
        ...state,
        selectedCustomerNames: Array.from(customerNames),
      }));
    },
    [setDashboardState]
  );

  const setSelectedFromCountyIDs = useCallback(
    (countyIDs: number[]) => {
      setDashboardState((state) => ({
        ...state,
        selectedFromCountyIDs: Array.from(countyIDs),
      }));
    },
    [setDashboardState]
  );

  const setSelectedToCountyIDs = useCallback(
    (countyIDs: number[]) => {
      setDashboardState((state) => ({
        ...state,
        selectedToCountyIDs: Array.from(countyIDs),
      }));
    },
    [setDashboardState]
  );

  const setGroupingLevel = useCallback(
    (level: GroupingLevel) => {
      setDashboardState((state) => ({
        ...state,
        groupingLevel: level,
      }));
    },
    [setDashboardState]
  );

  const setExcludeCompanyNameContains = useCallback(
    (pattern: string | null) => {
      setDashboardState((state) => ({
        ...state,
        excludeCompanyNameContains: pattern,
      }));
    },
    [setDashboardState]
  );

  const providerValue = useMemo(
    (): DashboardControlsProps<T> => ({
      dashboardState: {
        ...dashboardState,
        daterange,
      },
      setCollapsedState,
      setExpandedState,
      setWidgets,
      setSelectedDateRange,
      setSelectedBusinessUnits,
      setSelectedCaseTypes,
      setSearchText,
      setSelectedAssignmentTypes,
      setSelectedSupplierIDs,
      setSelectedCustomerNames,
      setSelectedFromCountyIDs,
      setSelectedToCountyIDs,
      setGroupingLevel,
      setExcludeCompanyNameContains,
    }),
    [
      dashboardState,
      daterange,
      setCollapsedState,
      setExpandedState,
      setWidgets,
      setSelectedDateRange,
      setSelectedBusinessUnits,
      setSelectedCaseTypes,
      setSearchText,
      setSelectedAssignmentTypes,
      setSelectedSupplierIDs,
      setSelectedCustomerNames,
      setSelectedFromCountyIDs,
      setSelectedToCountyIDs,
      setGroupingLevel,
      setExcludeCompanyNameContains,
    ]
  );

  return (
    <DashboardControlsContext.Provider value={providerValue}>
      {children}
    </DashboardControlsContext.Provider>
  );
};

export const useDashboardControls = <T,>(): DashboardControlsProps<T> => {
  const context = useContext(DashboardControlsContext);
  if (!context) {
    throw new Error(
      'useDashboardControls must be used within a DashboardControlsProvider'
    );
  }
  return context as DashboardControlsProps<T>;
};
